import React, { useState, useCallback, useRef } from 'react';
import { createRoot } from 'react-dom/client';

interface Dimensions {
  width: number;
  height: number;
}

interface UseContentDimensionsOptions {
  enabled?: boolean;
}

export function useContentDimensions(options: UseContentDimensionsOptions = {}) {
  const { enabled = true } = options;
  const [dimensions, setDimensions] = useState<Dimensions | null>(null);
  const [isMeasuring, setIsMeasuring] = useState(false);
  const measurementRef = useRef<HTMLDivElement | null>(null);
  const portalRoot = useRef<HTMLDivElement | null>(null);

  // Create portal root on demand
  const getPortalRoot = useCallback(() => {
    if (!portalRoot.current) {
      portalRoot.current = document.createElement('div');
      portalRoot.current.style.position = 'absolute';
      portalRoot.current.style.left = '-9999px';
      portalRoot.current.style.top = '-9999px';
      portalRoot.current.style.visibility = 'hidden';
      portalRoot.current.style.pointerEvents = 'none';
      document.body.appendChild(portalRoot.current);
    }
    return portalRoot.current;
  }, []);

  const measureContent = useCallback(
    (renderContent: () => React.ReactNode): Promise<Dimensions> => {
      return new Promise(resolve => {
        if (!enabled) {
          resolve({ width: 0, height: 0 });
          return;
        }

        setIsMeasuring(true);

        // Use requestAnimationFrame to ensure DOM updates
        requestAnimationFrame(() => {
          const container = getPortalRoot();

          // Create measurement wrapper
          const measurementWrapper = document.createElement('div');
          measurementWrapper.style.position = 'absolute';
          measurementWrapper.style.visibility = 'hidden';
          container.appendChild(measurementWrapper);

          // Render content and measure
          const content = renderContent();
          const root = createRoot(measurementWrapper);

          root.render(content as React.ReactElement);

          // Wait for next frame to ensure render is complete
          requestAnimationFrame(() => {
            const dimensions = {
              width: measurementWrapper.offsetWidth,
              height: measurementWrapper.offsetHeight,
            };

            // Cleanup
            root.unmount();
            container.removeChild(measurementWrapper);

            setDimensions(dimensions);
            setIsMeasuring(false);
            resolve(dimensions);
          });
        });
      });
    },
    [enabled, getPortalRoot],
  );

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (portalRoot.current && portalRoot.current.parentNode) {
        portalRoot.current.parentNode.removeChild(portalRoot.current);
      }
    };
  }, []);

  return {
    dimensions,
    isMeasuring,
    measureContent,
  };
}
